import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckSquare, Plus } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  SurfacePageBody,
  SurfacePageCard,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { PROJECT_COLOR_HEX } from '@/versions/v1/shared/project-colors';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import {
  projectModulesSeed,
  type ProjectTask,
  type TaskColumn,
} from '@/versions/v1/shared/project-modules-seed';
import {
  columnLabel,
  formatDueHint,
  groupTasksByColumn,
  nextTaskId,
  priorityColor,
  priorityLabel,
  sortTasksForList,
  TASK_COLUMNS,
} from './ProjectTasksScene.transform';

type ViewMode = 'board' | 'list';

const VIEW_TABS = [
  { id: 'board' as const, label: 'Board' },
  { id: 'list' as const, label: 'List' },
];

function TaskCard({ task, now }: { task: ProjectTask; now: number }) {
  return (
    <div className="rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface)] p-3 shadow-sm">
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full"
          style={{ backgroundColor: PROJECT_COLOR_HEX[priorityColor(task.priority)] }}
          title={priorityLabel(task.priority)}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            {task.title}
          </p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs text-muted">{formatDueHint(task.dueAt, now)}</span>
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200">
              {task.assigneeInitials}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProjectTasksScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);
  const seed = projectModulesSeed(projectId);
  const now = useMemo(() => Date.now(), []);

  const [tasks, setTasks] = useState<ProjectTask[]>(() => seed.tasks);
  const [viewMode, setViewMode] = useState<ViewMode>('board');

  useEffect(() => {
    setTasks(seed.tasks);
  }, [projectId]);

  useEffect(() => {
    document.title = project
      ? `Tasks · ${project.name} · Foundation`
      : 'Tasks · Foundation';
  }, [project]);

  const grouped = useMemo(() => groupTasksByColumn(tasks), [tasks]);
  const listRows = useMemo(() => sortTasksForList(tasks), [tasks]);

  const handleAddTask = (column: TaskColumn) => {
    setTasks((prev) => [
      ...prev,
      {
        id: nextTaskId(prev),
        title: 'New task',
        assigneeInitials: 'YO',
        dueAt: now + 7 * 24 * 60 * 60 * 1000,
        priority: 'medium',
        column,
      },
    ]);
  };

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Project not found" />
          <Link to="/projects" className="text-sm text-accent-600">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ProjectTasksScene">
        <SurfacePageBody width="full" className="space-y-6">
          <SurfacePageHeader
            kicker={project.name}
            title="Tasks"
            description="Tracked work items assigned to people or agents."
            tabs={
              <SurfacePageTabs
                items={VIEW_TABS}
                value={viewMode}
                onChange={setViewMode}
                aria-label="Tasks view"
              />
            }
            actions={<HelpAnchor doc="workspace/project-tasks" label="Tasks" />}
          />

          {tasks.length === 0 ? (
            <EmptyState
              icon={<CheckSquare className="mx-auto h-8 w-8 text-zinc-400" />}
              title="No tasks yet"
              description="Add tasks to track work across your team — use the board or list view once items exist."
              action={
                <button
                  type="button"
                  className="btn-primary inline-flex h-9 items-center gap-1.5 px-4 text-sm"
                  onClick={() => handleAddTask('todo')}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add task
                </button>
              }
            />
          ) : viewMode === 'list' ? (
            <SurfacePageCard className="overflow-hidden p-0">
              <Table wrap={false} className="min-w-[40rem] text-left">
                <THead className="border-b border-[var(--glass-border)] bg-transparent">
                  <TR>
                    <TH className="px-5 py-3">Task</TH>
                    <TH>Status</TH>
                    <TH>Assignee</TH>
                    <TH>Due</TH>
                    <TH>Priority</TH>
                  </TR>
                </THead>
                <TBody className="divide-y divide-[var(--glass-border)]">
                  {listRows.map((task) => (
                    <TR key={task.id} className="border-0">
                      <TD className="px-5 py-3.5 font-medium text-zinc-900 dark:text-zinc-100">
                        <div className="flex items-center gap-2">
                          <span
                            aria-hidden
                            className="h-2 w-2 flex-shrink-0 rounded-full"
                            style={{
                              backgroundColor: PROJECT_COLOR_HEX[priorityColor(task.priority)],
                            }}
                          />
                          {task.title}
                        </div>
                      </TD>
                      <TD className="py-3.5 text-muted">{columnLabel(task.column)}</TD>
                      <TD className="py-3.5">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200">
                          {task.assigneeInitials}
                        </span>
                      </TD>
                      <TD className="py-3.5 text-muted">
                        {formatDueHint(task.dueAt, now)}
                      </TD>
                      <TD className="py-3.5 text-muted">{priorityLabel(task.priority)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </SurfacePageCard>
          ) : (
            <div className="grid gap-4 lg:grid-cols-3">
              {TASK_COLUMNS.map((col) => (
                <section key={col.id} className="flex flex-col gap-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <h2 className="text-xs font-medium uppercase tracking-wide text-muted">
                      {col.label}
                    </h2>
                    <span className="text-xs text-muted">{grouped[col.id].length}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {grouped[col.id].map((task) => (
                      <TaskCard key={task.id} task={task} now={now} />
                    ))}
                  </div>
                  <button
                    type="button"
                    className={cn(
                      'inline-flex h-9 items-center justify-center gap-1.5 rounded-lg',
                      'border border-dashed border-[var(--glass-border)] text-xs font-medium text-muted',
                      'transition-colors hover:border-[color-mix(in_srgb,var(--color-accent-500)_40%,transparent)] hover:text-zinc-700 dark:hover:text-zinc-300',
                    )}
                    onClick={() => handleAddTask(col.id)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add task
                  </button>
                </section>
              ))}
            </div>
          )}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
